// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";
import Button from "@components/Button/Button";
import StackLayout from "@components/layouts/StackLayout/StackLayout";

// HELPERS
import {createClassName, loop} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./Poa.fulfillment";
import Validator from "../../../../../classes/Validator";
import Checkbox from "../../../../../components/Checkbox/Checkbox";

// Partner specific config
import poaConfig from "../PoaConfig";

export default class CobPoa extends React.PureComponent {

	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired
	};

	static companyExclusive = true;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields || prevState.numberOfCompanyReps !== state.numberOfCompanyReps) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	render() {

		const {props, state} = this;
		const {title, partner} = props;

		const classes = createClassName("Cob-Poa", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled
		});

		const companyRepsList = [];
		let hasAtLeastOneSignatory = false;
		for (let i = 0; i < this.state.numberOfCompanyReps; i++) {
			if (state.fields["checkbox_signatory_" + i] === "yes" || state.fields["checkbox_poa_" + i] === "yes") {
				hasAtLeastOneSignatory = true;
			}

			companyRepsList.push(
				<div key={i} className="rep">
					<h4>Företrädare {i + 1}</h4>

					<div className="rep-type" style={{display: "none"}}>
						<Checkbox
							name={"checkbox_signatory_" + i}
							label="Firmatecknare"
							value="yes"
							checked={state.fields["checkbox_signatory_" + i] === "yes"}
						/>
					</div>

					<FieldsCollection
						fieldsPerRow={2}
						fields={[
							{
								render: (
									<EditableField
										name={"company_representative_firstname_" + i}
										label="Förnamn"
										value={state.fields["company_representative_firstname_" + i]}
										valid={state.validFields["company_representative_firstname_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										name={"company_representative_lastname_" + i}
										label="Efternamn"
										value={state.fields["company_representative_lastname_" + i]}
										valid={state.validFields["company_representative_lastname_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										name={"company_representative_sec_num_" + i}
										label="Personnummer"
										placeholder="ÅÅÅÅMMDD-XXXX"
										value={state.fields["company_representative_sec_num_" + i]}
										valid={state.validFields["company_representative_sec_num_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										type="email"
										name={"company_representative_email_" + i}
										label="E-postadress"
										value={state.fields["company_representative_email_" + i]}
										valid={state.validFields["company_representative_email_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}
						]}
					/>

					{poaConfig[partner.name] && poaConfig[partner.name].showAttachFullmaktWarning && ((poaConfig[partner.name].showFullmakt && state.fields["checkbox_poa_" + i] === "yes") || (!poaConfig[partner.name].showFullmakt && state.fields["checkbox_mng_" + i] === "yes")) && (
						<div className="Notice warning" style={{marginTop: "20px"}}>
							Var god notera att fullmakten måste bifogas som PDF under "Övriga dokument" nedan.
						</div>
					)}
				</div>
			);
		}

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					{(!hasAtLeastOneSignatory) && (
						<div className="Notice warning" style={{margin: "0 0 20px 0"}}>
							<ul style={{width: "100%", padding: "0", margin: "0"}}>
								{!hasAtLeastOneSignatory && (
									<li style={{alignItems: "flex-start", width: "100%", padding: "10px 0"}}>
										<React.Fragment>
											Åtminstone en signatär måste anges.
										</React.Fragment>
									</li>
								)}
							</ul>
						</div>
					)}

					{companyRepsList}

					{this.state.numberOfCompanyReps <= 4 && (
						<StackLayout fullWidth>
							<Button
								size="small"
								label="Ta bort sista"
								appearance={this.state.numberOfCompanyReps <= 1 ? "medium" : "danger"}
								disabled={this.state.numberOfCompanyReps <= 1}
								onClick={() => this.setState({numberOfCompanyReps: this.state.numberOfCompanyReps - 1})}
							/>

							<Button
								size="small"
								label="Lägg till"
								appearance={this.state.numberOfCompanyReps >= 4 ? "medium" : "primary"}
								disabled={this.state.numberOfCompanyReps >= 4}
								onClick={() => this.setState({numberOfCompanyReps: this.state.numberOfCompanyReps + 1})}
								filled
							/>
						</StackLayout>
					)}
				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name.indexOf("company_representative_email_") > -1) {
			return Validator.validateEmail(value);
		}

		if (name.indexOf("company_representative_sec_num_") > -1) {
			return Validator.validateSsn(value, false);
		}

		return value.length > 0;
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		// Up to 4 company signatories supported on the form
		...(() => {
			const returnValue = [];
			loop(4, i => {
				returnValue.push("checkbox_signatory_" + i);
				returnValue.push("company_representative_firstname_" + i);
				returnValue.push("company_representative_lastname_" + i);
				returnValue.push("company_representative_sec_num_" + i);
				returnValue.push("company_representative_email_" + i);
			});
			return returnValue;
		})()
	];
}

function _getInitialState() {
	if (this.props.prevState) {
		return {...this.props.prevState};
	}

	const initialFieldsState = {
		"checkbox_signatory_0": "yes",
		"company_representative_firstname_0": "",
		"company_representative_lastname_0": "",
		"company_representative_sec_num_0": "",
		"company_representative_email_0": "",

		"checkbox_signatory_1": "yes",
		"company_representative_firstname_1": "",
		"company_representative_lastname_1": "",
		"company_representative_sec_num_1": "",
		"company_representative_email_1": "",

		"checkbox_signatory_2": "yes",
		"company_representative_firstname_2": "",
		"company_representative_lastname_2": "",
		"company_representative_sec_num_2": "",
		"company_representative_email_2": "",

		"checkbox_signatory_3": "yes",
		"company_representative_firstname_3": "",
		"company_representative_lastname_3": "",
		"company_representative_sec_num_3": "",
		"company_representative_email_3": "",
	};

	return {
		numberOfCompanyReps: 1,
		validFields: {},
		fields: initialFieldsState
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Poa/glofitech-ag/Poa.js